import { useEffect, useState, useMemo, useRef } from "react"
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify';

import RichText from './RichText'

import css from './sass/markdown.module.scss'
import themeCss from '../components/sass/theme.module.scss'

// replaces:
// \n with <p></p>
// [link text](https://site.com?foo=bar) with <a target="_blank" rel="noreferrer" href="https://site.com?foo=bar">link text</a>

export default function CustomMarkdown({text, cssClass, noLineBreaks, linkObj}) {

  if (text === null) text = ''

  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const isHtmlString = (value) => typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);


  useEffect(() => {
    const checkIfTruncated = () => {
      if (textRef.current) {
        const currentText = textRef.current;
        const scrollHeight = currentText.scrollHeight; // full content height
        const clientHeight = currentText.clientHeight; // display height

        // set value for if the content height exceeds the visible height
        setIsTruncated(scrollHeight > clientHeight);
      }
    };

    checkIfTruncated();
    window.addEventListener('resize', checkIfTruncated); // Check on resize

    return () => {
      window.removeEventListener('resize', checkIfTruncated); // Cleanup
    };
  }, []);

  // check if text is coming from new text editor
  if (text.startsWith('<p>') || text.startsWith('<ul><li><p>')) return <RichText text={text} cssClass={cssClass} noLineBreaks={noLineBreaks}linkObj={linkObj} />

  const doAllReplaces = (text) => {
    const step1 = doLinkReplace(text) // this one takes a stirng, all the other parse methods take an array of text strings
    const step2 = doItalicsReplace(step1) // make sure the most atomic thing goes last (IE that can't have another element we're parsing inside of it)

    return step2
  }

  const doLinkReplace = text => {
    const preParsedText = text
                        .replaceAll('|', '&&&pipe&&&')
                        .replaceAll('~', '&&&squiggly&&&')
                        .replace(/\[([^[]+)\](\(([^)]*))\)/gim, '|$3~$1|')
                        .split('|')

    const reAddPipesAndSqugglies = oldText => {
      return oldText.replaceAll('&&&pipe&&&', '|').replaceAll('&&&squiggly&&&', '~')
    }

    const finalParsedText = preParsedText.map((parsedSection, index) => {
      if (parsedSection.indexOf('~') > -1) {
        const [ href, linkText ] = parsedSection.split('~')
        return (<a key={index} target="_blank" rel="noreferrer" href={reAddPipesAndSqugglies(href)}>{reAddPipesAndSqugglies(linkText)}</a>)
      }
      else {
        return reAddPipesAndSqugglies(parsedSection)
      }
    })

    return finalParsedText
  }

  const doItalicsReplace = textArray => {
    const finalParsedArray = []

    textArray.forEach(section => {
      // already a react element
      if (section.props) {
        finalParsedArray.push(section)
      }
      else {
        const italicsArray = section.split('<i>')

        italicsArray.forEach(parsedSection => {
          const [toMaybeItalicize, remainder] = parsedSection.split('</i>')

          if (remainder) {
            finalParsedArray.push(<i>{toMaybeItalicize}</i>)
            finalParsedArray.push(remainder)
          }
          else {
            finalParsedArray.push(toMaybeItalicize)
          }
        })
      }
    })

    return finalParsedArray
  }

  const linkElement = <Link className={`${css['link-toitem']} ${themeCss['bold-nodecoration']}` } to={{ pathname: linkObj?.browse ? '/browse/item' : '/auction/item', search: `?auctionId=${linkObj?.auctionCode}&itemId=${linkObj?.itemCode}`}} onClick={linkObj?.onClick}>
      More...
    </Link>
  
  const fixedLinkElement = <Link className={`${css['link-toitem']} ${themeCss['bold-nodecoration']}` } to={{ pathname: linkObj?.browse ? '/browse/fixeditem' : '/auction/fixeditem', search: `?auctionId=${linkObj?.auctionCode}&fixedItemId=${linkObj?.itemCode}`}} onClick={linkObj?.onClick}>
      More...
    </Link>

  return (
    <> 
    {isHtmlString(text) ? (
      // to render trusted-looking rich text HTML as actual HTML in React
      <div
        ref={textRef}
        className={cssClass || 'none'}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(text),
        }}
      />
    ) : (
      <>
      {noLineBreaks &&
        <p ref={textRef} className={cssClass || 'none'}>{doAllReplaces(text).map((section, index) => <span key={index}>{section}</span>)}</p>
      }
      {!noLineBreaks &&
        text.replaceAll('\n\n', '\n').split('\n').map((paragraph, index) =>
          <p key={index} ref={textRef} className={cssClass || 'none'}>{doAllReplaces(paragraph).map((section, pindex) => <span key={pindex}>{section}</span>)}</p>
        )
      }
      </>
      )}
      {isTruncated && linkObj?.link === 'itemPage' ? linkElement : ''}
      {isTruncated && linkObj?.link === 'fixedItemPage' ? fixedLinkElement : ''}
    </>
  )
}
